// Google's review composer takes only a place reference. There is NO supported
// URL parameter for pre-filling the review text or the star rating — Google
// removed every such mechanism to keep reviews first-party. So the text is put on
// the traveller's clipboard instead and the composer is opened ready to paste.
//
// Two ways to point this at the real business, in priority order:
//   VITE_GOOGLE_REVIEW_URL      — paste the short link straight from Google
//                                 Business Profile → "Ask for reviews"
//                                 (looks like https://g.page/r/XXXXXXXX/review).
//   VITE_GOOGLE_REVIEW_PLACE_ID — the Business Profile Place ID.
const GOOGLE_REVIEW_URL = import.meta.env.VITE_GOOGLE_REVIEW_URL;

export const GOOGLE_REVIEW_PLACE_ID =
  import.meta.env.VITE_GOOGLE_REVIEW_PLACE_ID || "REPLACE_WITH_PLACE_ID";

/** True while neither env var is set — the UI uses this to stay honest about it. */
export const isGoogleReviewConfigured = () =>
  Boolean(GOOGLE_REVIEW_URL) || GOOGLE_REVIEW_PLACE_ID !== "REPLACE_WITH_PLACE_ID";

export function getGoogleReviewUrl() {
  // A Business Profile short link opens the composer directly — preferred, and
  // the only option that needs no Place ID lookup.
  if (GOOGLE_REVIEW_URL) return GOOGLE_REVIEW_URL;

  if (GOOGLE_REVIEW_PLACE_ID !== "REPLACE_WITH_PLACE_ID") {
    return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(GOOGLE_REVIEW_PLACE_ID)}`;
  }

  // Nothing configured — a search results page beats a guaranteed 404 on the
  // write-review endpoint. Once either env var is set this branch never runs.
  return "https://www.google.com/maps/search/?api=1&query=RoamMyWay";
}

/**
 * Puts the review text on the clipboard. Call this INSIDE the click handler:
 * Safari (and Firefox with clipboard prompts) reject writeText once the user
 * gesture has been spent by an await, which is why the previous placement —
 * after the submit request resolved — copied nothing on those browsers.
 */
export function copyReviewText(reviewText) {
  if (!reviewText || typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
    return Promise.resolve(false);
  }
  return navigator.clipboard.writeText(reviewText).then(
    () => true,
    () => false,
  );
}

/**
 * Opens a blank tab synchronously, in direct response to a user gesture
 * (e.g. inside a button onClick), so browsers don't treat the later
 * navigation as a blocked popup once the async submit request resolves.
 */
export function openGoogleReviewHandoffWindow() {
  if (typeof window === "undefined") return null;
  try {
    return window.open("", "_blank");
  } catch {
    return null;
  }
}

/**
 * Copies the traveller's own review text to their clipboard (best-effort —
 * Google's composer opens empty regardless, so this saves them retyping)
 * and navigates the already-open handoff tab to Google's review composer.
 *
 * Returns a Promise resolving to { opened, copied } — copied reflects the
 * clipboard write's actual outcome, not just that it was attempted — so the
 * caller can tell the traveller what actually happened: a blocked popup
 * needs a manual link, and an un-copied review needs them to know the text
 * wasn't put on the clipboard.
 */
export async function completeGoogleReviewHandoff(handoffWindow, reviewText) {
  const result = { opened: false, copied: false };

  // Retry the copy here for browsers that allow it post-gesture; the reliable
  // attempt already happened on click via copyReviewText.
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText && reviewText) {
    result.copied = await navigator.clipboard.writeText(reviewText).then(
      () => true,
      () => false,
    );
  }

  if (!handoffWindow || handoffWindow.closed) return result;

  try {
    handoffWindow.location.href = getGoogleReviewUrl();
    result.opened = true;
  } catch {
    // Window may have been closed or navigated cross-origin already — nothing else to do.
  }

  return result;
}

/** Closes the pre-opened handoff tab when submission fails or is blocked. */
export function cancelGoogleReviewHandoff(handoffWindow) {
  if (handoffWindow && !handoffWindow.closed) {
    try {
      handoffWindow.close();
    } catch {
      // Ignore — best-effort cleanup only.
    }
  }
}